import { Button, Card, DateRangePicker, Input, Tab } from '@features/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TTab } from '@src/types';
import { AccountSubscriptions } from '@features/user/billing';
import { AccountReceipts } from '@features/user/billing/AccountReceipts.tsx';
import { DateValueType } from '@lib/utils.ts';
import { SearchIcon } from 'lucide-react';
import { CalendarIcon } from '@features/ui/icons';
import {
  cancelBillingsSubscriptions,
  checkoutRenewBillingPackage,
  getBillingRenewPackage,
  getBillingsReceipts,
  getBillingsSubscriptions,
} from '@src/services/billing.ts';
import {
  BillingReceiptsReq,
  BillingRenewAddon,
  BillingRenewPackage,
  BillingStatus,
  InvoiceSubscriptionRead,
  TAccountReceiptsTable,
} from '@src/types/billing.ts';
import { ConfirmCancelPopup } from '@features/user/billing/ConfirmCancelPopup.tsx';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { useGetMe } from '@src/hooks/useGetMe';
import { PaymentFormValues } from '@src/types/packages';
import { PurchaseResetPackage } from '@src/types/purchaseReset';
import { toast } from 'react-toastify';
import { BillingRenew } from '@src/features/user/billing/BillingRenew';

export const BillingPage = () => {
  const [tabActive, setTabActive] = useState(0);
  const [searchVal, setSearchVal] = useState('');
  const [selectedBillingSubId, setSelectedBillingSubId] = useState<
    number | null
  >(null);
  const [valueDate, setValueDate] = useState<DateValueType>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [billingsSubscriptions, setBillingsSubscriptions] = useState<
    InvoiceSubscriptionRead[]
  >([]);
  const [billingsReceipts, setBillingsReceipts] = useState<
    TAccountReceiptsTable[]
  >([]);
  const [renewBillingOpen, setRenewBillingOpen] = useState(false);
  const [renewBillingLoading, setRenewBillingLoading] = useState(false);
  const [renewBillingSubmitting, setRenewBillingSubmitting] = useState(false);
  const [renewBillingPackage, setRenewBillingPackage] =
    useState<BillingRenewPackage | null>(null);
  const [selectedBillingId, setSelectedBillingId] = useState<number | null>(
    null
  );
  const { setIsPurchaseMode, setPlatform } = useCheckoutStore();
  const { me } = useGetMe();

  const navigate = useNavigate();

  const fetchBillingsSubscriptions = useCallback(async () => {
    const res = await getBillingsSubscriptions();

    const billingsSorts = res.data.sort((a, b) => {
      const aBillingsId = Number(a?.invoice_id);
      const bBillingsId = Number(b?.invoice_id);

      if (aBillingsId > bBillingsId) {
        return 1;
      }
      if (aBillingsId < bBillingsId) {
        return -1;
      }

      return 0;
    });

    setBillingsSubscriptions(billingsSorts);
  }, []);

  const fetchBillingsReceipts = useCallback(
    async (params?: BillingReceiptsReq) => {
      const res = await getBillingsReceipts(params);

      setBillingsReceipts(res.data);
    },
    []
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        await fetchBillingsReceipts({
          start_date: valueDate?.startDate
            ? new Date(valueDate?.startDate).toISOString()
            : null,
          end_date: valueDate?.endDate
            ? new Date(valueDate.endDate).toISOString()
            : null,
          search_words: searchValue,
        });
      }, 500),
    [valueDate?.startDate, valueDate?.endDate, fetchBillingsReceipts]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBillingsSubscriptions().then();
  }, [fetchBillingsSubscriptions]);

  useEffect(() => {
    fetchBillingsReceipts().then();
  }, [fetchBillingsReceipts]);

  const billingsSubscriptionsActive = useMemo(
    () =>
      billingsSubscriptions.filter(
        (item) => item.subscription_status === BillingStatus.Active
      ),
    [billingsSubscriptions]
  );

  const billingsSubscriptionsExpiring = useMemo(
    () =>
      billingsSubscriptions.filter(
        (item) =>
          item.subscription_status === BillingStatus.Expired ||
          item.subscription_status === BillingStatus.Canceled
      ),
    [billingsSubscriptions]
  );

  const handleCancelBilling = useCallback(async (billingSubId: number) => {
    if (!billingSubId) return;
    setSelectedBillingSubId(billingSubId);
    setConfirmCancel(true);
  }, []);

  const handleConfirmCancelBillings = useCallback(async () => {
    if (!selectedBillingSubId) return;
    await cancelBillingsSubscriptions(selectedBillingSubId);
    await fetchBillingsSubscriptions();
    setSelectedBillingSubId(0);
  }, [fetchBillingsSubscriptions, selectedBillingSubId]);

  const handleChangeDate = useCallback(
    async (date: DateValueType) => {
      setValueDate(date);

      if (!date) {
        await fetchBillingsReceipts({
          start_date: null,
          end_date: null,
          search_words: searchVal,
        });

        return;
      }

      if (date?.startDate && date?.endDate) {
        await fetchBillingsReceipts({
          start_date: new Date(date.startDate).toISOString(),
          end_date: new Date(date.endDate).toISOString(),
          search_words: searchVal,
        });
      }
    },
    [fetchBillingsReceipts, searchVal]
  );

  const handleChangeSearchWords = useCallback(
    async (val: string) => {
      setSearchVal(val);
      debouncedSearch(val);
    },
    [debouncedSearch]
  );

  const handleStartNewChallenge = useCallback(() => {
    setPlatform(me?.platform || '');
    setIsPurchaseMode(true);
    navigate('/select-trading-account-type');
  }, [me?.platform, navigate, setIsPurchaseMode, setPlatform]);

  const handleRenew = useCallback(async (billingId: number) => {
    if (!billingId) {
      toast.error('Invalid billing subscription');

      return;
    }

    setSelectedBillingId(billingId);
    setRenewBillingLoading(true);
    setRenewBillingOpen(true);

    try {
      const response = await getBillingRenewPackage(billingId);

      setRenewBillingPackage(response.data);
    } catch (error) {
      console.error('Failed to fetch renew billing quote:', error);
      toast.error('Failed to load renewal details. Please try again.');
      setRenewBillingOpen(false);
    } finally {
      setRenewBillingLoading(false);
    }
  }, []);

  const handleRenewBillingSubmit = useCallback(
    async (values: PaymentFormValues) => {
      if (!selectedBillingId || !renewBillingPackage) return;

      setRenewBillingSubmitting(true);

      try {
        const addonIds = (renewBillingPackage.addons || [])
          .map((a) => a.product?.id)
          .filter((id): id is number => typeof id === 'number');

        const addonPaymentHandlingType: 'monthly' | 'non_recurring' | null =
          addonIds.length
            ? renewBillingPackage.addons?.some(
                (a: BillingRenewAddon) => a.payment_handling_type === 'monthly'
              )
              ? 'monthly'
              : 'non_recurring'
            : null;

        const payload = {
          package_id: renewBillingPackage.product.id,
          addon_ids: addonIds,
          addon_payment_handling_type: addonPaymentHandlingType,
          payment_gateway: values.payment_type,
          coupon_code: values.coupon_code,
          billing_info: {
            first_name: values.first_name,
            last_name: values.last_name,
            street: values.street,
            city: values.city,
            country: values.country,
            state: values.state,
            zip_code: values.zip_code,
            phone_number: values.phone_number,
          },
        };

        const response = await checkoutRenewBillingPackage(
          selectedBillingId,
          payload
        );

        if (response.data?.url) {
          window.location.href = response.data.url;
        } else {
          toast.success('Renewal completed successfully!');
          setRenewBillingOpen(false);
          await fetchBillingsSubscriptions();
        }
      } catch (error: any) {
        console.error('Failed to submit renewal:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'Failed to process renewal. Please try again.';

        toast.error(errorMessage);
      } finally {
        setRenewBillingSubmitting(false);
      }
    },
    [
      fetchBillingsSubscriptions,
      renewBillingPackage,
      selectedBillingId,
      setRenewBillingOpen,
    ]
  );

  const tabs: TTab[] = useMemo(
    () => [
      {
        label: 'All',
        component: (
          <AccountSubscriptions
            data={billingsSubscriptions}
            onCancel={handleCancelBilling}
            onRenew={handleRenew}
          />
        ),
      },
      {
        label: 'Active',
        component: (
          <AccountSubscriptions
            data={billingsSubscriptionsActive}
            onCancel={handleCancelBilling}
            onRenew={handleRenew}
          />
        ),
      },
      {
        label: 'Expiring',
        component: (
          <AccountSubscriptions
            data={billingsSubscriptionsExpiring}
            onCancel={handleCancelBilling}
            onRenew={handleRenew}
          />
        ),
      },
    ],
    [
      handleRenew,
      billingsSubscriptions,
      billingsSubscriptionsActive,
      billingsSubscriptionsExpiring,
      handleCancelBilling,
    ]
  );

  return (
    <div className={'min-w-[900px] flex flex-col gap-y-5'}>
      <div className={'flex justify-between items-center'}>
        <h1 className={'text-[23px] font-medium'}>Account Subscriptions</h1>
        <Button
          className={'font-bold'}
          size={'lg'}
          variant={'gradient'}
          onClick={handleStartNewChallenge}
        >
          Start New Challenge
        </Button>
      </div>
      <Card>
        <Tab
          className={'mt-2 mb-5'}
          fontWeight={700}
          tabActive={tabActive}
          tabs={tabs}
          variant={'gradient'}
          onChangeTab={setTabActive}
        />
      </Card>
      <h1 className={'text-[23px]'}>Account Receipts</h1>
      <Card className={'flex flex-col gap-y-5'}>
        <div className={'flex justify-between'}>
          <DateRangePicker
            className={'px-2 rounded-[10px]'}
            color={'#979797'}
            rightIcon={<CalendarIcon fill={'#979797'} />}
            size={'lg'}
            value={valueDate}
            variant={'outline'}
            onChange={handleChangeDate}
          />
          <Input
            className={
              'max-w-[242px] min-w-[200px] border-[#979797] rounded-[10px] h-[44px]'
            }
            endIcon={<SearchIcon color={'#8B8B8B'} size={15} />}
            placeholder={'Search'}
            textSize={'sm'}
            value={searchVal}
            onChange={(event) => handleChangeSearchWords(event.target.value)}
          />
        </div>
        <AccountReceipts data={billingsReceipts} />
      </Card>
      <ConfirmCancelPopup
        open={confirmCancel}
        onConfirm={handleConfirmCancelBillings}
        onOpenChange={setConfirmCancel}
      />
      <BillingRenew
        addons={renewBillingPackage?.addons ?? []}
        loading={renewBillingLoading}
        open={renewBillingOpen}
        renewPackage={
          renewBillingPackage
            ? ({
                ...renewBillingPackage.product,
                billing_plan: renewBillingPackage.billing_plan ?? undefined,
              } as PurchaseResetPackage)
            : null
        }
        submitting={renewBillingSubmitting}
        onOpenChange={setRenewBillingOpen}
        onSubmit={handleRenewBillingSubmit}
      />
    </div>
  );
};
